/**
 * 配信シリーズ API hooks.
 *
 * GET/POST/PATCH/DELETE /api/delivery-series
 */

import { RestApiError, restClient } from "lib/rest-client";
import { useCallback, useEffect, useState } from "react";

export type DeliverySeriesStatus = "active" | "archived";

export type DeliverySeriesStats = {
  deliveryCount: number;
  totalRecipients: number;
  totalReplies: number;
  unresponded: number;
  inProgress: number;
  completed: number;
  responseRate: number;
  lastSentAt: string | null;
  /** 延べ到達数 (延べ返信率の分母) */
  deliveredTotal: number;
  /** 計測済み延べ到達数 (延べ開封率・延べクリック率の分母) */
  measuredDeliveredTotal: number;
  /** 返信のあった延べ宛先数 */
  replyRecipients: number;
  /** 延べ開封率 (計測済み配信が無ければ null) */
  openRate: number | null;
  /** 延べクリック率 (計測済み配信が無ければ null) */
  clickRate: number | null;
  /** 延べ返信率 */
  replyRate: number | null;
};

/** A2: シリーズ配下の配信 1 件分の内訳 */
export type SeriesDeliveryBreakdown = {
  deliveryId: string;
  subject: string;
  sentAt: string | null;
  measured: boolean;
  sent: number;
  delivered: number;
  uniqueOpens: number | null;
  uniqueClicks: number | null;
  replyRecipients: number;
  openRate: number | null;
  clickRate: number | null;
  replyRate: number | null;
};

/** A2: シリーズの分析（通算値はすべて「延べ」） */
export type DeliverySeriesAnalytics = {
  seriesId: string;
  includeBots: boolean;
  deliveryCount: number;
  totalRecipients: number;
  deliveredTotal: number;
  measuredDeliveryCount: number;
  measuredDeliveredTotal: number;
  uniqueOpens: number;
  uniqueClicks: number;
  replyRecipients: number;
  replyMessages: number;
  openRate: number | null;
  clickRate: number | null;
  replyRate: number | null;
  deliveries: SeriesDeliveryBreakdown[];
  excludedBotOpenRecipients: number;
  excludedBotClickRecipients: number;
};

export type DeliverySeries = {
  id: string;
  name: string;
  description: string | null;
  status: DeliverySeriesStatus;
  talentId: string | null;
  talentName: string | null;
  focusMatterId: string | null;
  focusMatterTitle: string | null;
  createdBy: string;
  creatorName: string | null;
  stats: DeliverySeriesStats;
  createdAt: string;
  updatedAt: string;
};

type DeliverySeriesListResponse = {
  items: DeliverySeries[];
  total: number;
  page: number;
  limit: number;
};

export type CreateDeliverySeriesInput = {
  name: string;
  description?: string | null;
  talentId?: string | null;
  focusMatterId?: string | null;
};

export type UpdateDeliverySeriesInput = {
  name?: string;
  description?: string | null;
  status?: DeliverySeriesStatus;
  talentId?: string | null;
  focusMatterId?: string | null;
};

/**
 * シリーズステータスの日本語ラベル
 * @param {DeliverySeriesStatus} status ステータス
 * @return {string} 表示ラベル
 */
export function deliverySeriesStatusLabel(
  status: DeliverySeriesStatus,
): string {
  return status === "active" ? "active" : "archived";
}

export type UseDeliverySeriesListOptions = {
  page?: number;
  limit?: number;
  status?: DeliverySeriesStatus | null;
  search?: string | null;
};

/**
 * シリーズ一覧取得 hook
 * @param {UseDeliverySeriesListOptions} options ページング/絞込
 * @return {object} items, total, loading, error, refetch
 */
export function useDeliverySeriesList(
  options: UseDeliverySeriesListOptions = {},
): {
  items: DeliverySeries[];
  total: number;
  loading: boolean;
  error: RestApiError | null;
  refetch: () => Promise<void>;
} {
  const { page = 1, limit = 50, status, search } = options;
  const [items, setItems] = useState<DeliverySeries[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<RestApiError | null>(null);

  const fetchList = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params: Record<string, string> = {
        page: String(page),
        limit: String(limit),
      };
      if (status) params.status = status;
      if (search) params.search = search;
      const response = await restClient.get<DeliverySeriesListResponse>(
        "/delivery-series",
        params,
      );
      setItems(response.items);
      setTotal(response.total);
    } catch (e) {
      setError(toApiError(e));
    } finally {
      setLoading(false);
    }
  }, [page, limit, status, search]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  return { items, total, loading, error, refetch: fetchList };
}

/**
 * 単一シリーズ取得 hook
 * @param {string|null} seriesId シリーズ ID
 * @return {object} series, loading, error, refetch
 */
export function useDeliverySeries(seriesId: string | null): {
  series: DeliverySeries | null;
  loading: boolean;
  error: RestApiError | null;
  refetch: () => Promise<void>;
} {
  const [series, setSeries] = useState<DeliverySeries | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<RestApiError | null>(null);

  const fetchOne = useCallback(async () => {
    if (!seriesId) {
      setSeries(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const response = await restClient.get<DeliverySeries>(
        `/delivery-series/${seriesId}`,
      );
      setSeries(response);
    } catch (e) {
      setError(toApiError(e));
    } finally {
      setLoading(false);
    }
  }, [seriesId]);

  useEffect(() => {
    fetchOne();
  }, [fetchOne]);

  return { series, loading, error, refetch: fetchOne };
}

/**
 * シリーズの分析 (A2) 取得 hook
 * @param {string|null} seriesId シリーズ ID
 * @return {object} analytics, loading, error
 */
export function useDeliverySeriesAnalytics(seriesId: string | null): {
  analytics: DeliverySeriesAnalytics | null;
  loading: boolean;
  error: RestApiError | null;
} {
  const [analytics, setAnalytics] = useState<DeliverySeriesAnalytics | null>(
    null,
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<RestApiError | null>(null);

  const fetchAnalytics = useCallback(async () => {
    if (!seriesId) {
      setAnalytics(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const response = await restClient.get<DeliverySeriesAnalytics>(
        `/delivery-series/${seriesId}/analytics`,
      );
      setAnalytics(response);
    } catch (e) {
      setError(toApiError(e));
    } finally {
      setLoading(false);
    }
  }, [seriesId]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  return { analytics, loading, error };
}

/**
 * シリーズ新規作成 hook
 * @return {object} create, loading, error
 */
export function useCreateDeliverySeries(): {
  create: (input: CreateDeliverySeriesInput) => Promise<DeliverySeries>;
  loading: boolean;
  error: RestApiError | null;
} {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<RestApiError | null>(null);

  const create = useCallback(async (input: CreateDeliverySeriesInput) => {
    setLoading(true);
    setError(null);
    try {
      const response = await restClient.post<DeliverySeries>(
        "/delivery-series",
        input,
      );
      return response;
    } catch (e) {
      const err = toApiError(e);
      setError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return { create, loading, error };
}

/**
 * シリーズ更新 hook
 * @return {object} update, loading, error
 */
export function useUpdateDeliverySeries(): {
  update: (
    seriesId: string,
    input: UpdateDeliverySeriesInput,
  ) => Promise<DeliverySeries>;
  loading: boolean;
  error: RestApiError | null;
} {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<RestApiError | null>(null);

  const update = useCallback(
    async (seriesId: string, input: UpdateDeliverySeriesInput) => {
      setLoading(true);
      setError(null);
      try {
        const response = await restClient.patch<DeliverySeries>(
          `/delivery-series/${seriesId}`,
          input,
        );
        return response;
      } catch (e) {
        const err = toApiError(e);
        setError(err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { update, loading, error };
}

/**
 * シリーズ削除 hook
 * @return {object} remove, loading, error
 */
export function useDeleteDeliverySeries(): {
  remove: (seriesId: string) => Promise<void>;
  loading: boolean;
  error: RestApiError | null;
} {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<RestApiError | null>(null);

  const remove = useCallback(async (seriesId: string) => {
    setLoading(true);
    setError(null);
    try {
      await restClient.delete<unknown>(`/delivery-series/${seriesId}`);
    } catch (e) {
      const err = toApiError(e);
      setError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return { remove, loading, error };
}

/**
 * 既存配信をシリーズに加入させる hook
 * @return {object} attach, loading, error
 */
export function useAttachDeliveriesToSeries(): {
  attach: (seriesId: string, deliveryIds: string[]) => Promise<number>;
  loading: boolean;
  error: RestApiError | null;
} {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<RestApiError | null>(null);

  const attach = useCallback(
    async (seriesId: string, deliveryIds: string[]) => {
      setLoading(true);
      setError(null);
      try {
        const response = await restClient.post<{ attachedCount: number }>(
          `/delivery-series/${seriesId}/attach-deliveries`,
          { deliveryIds },
        );
        return response.attachedCount;
      } catch (e) {
        const err = toApiError(e);
        setError(err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { attach, loading, error };
}

// ============================================================
// 一括再配信 (ISSUE-1017。契約は docs/design-docs/deliveries/bulk-resend.md §10)
// ============================================================

/**
 * 1 回の一括再配信で選べるシリーズの上限.
 * sendDelivery の待ち行列への暫定対応で 5 件にしている (設計上の上限は 20 件)。
 * 待ち行列の対策が別 ISSUE で終わったら 20 件に戻す。backend の上限と揃えること
 */
export const BULK_RESEND_MAX_SERIES = 5;

/** 一括再配信で上書きする件名の最大文字数 (trim 後。API も同じ上限で検証する) */
export const BULK_RESEND_SUBJECT_MAX_LENGTH = 500;

export type BulkResendEligibility =
  | "ok"
  | "archived"
  | "not_found"
  | "no_template"
  | "template_group_deleted"
  | "sender_unverified"
  | "no_recipients";

export type BulkResendTemplate = {
  deliveryId: string;
  sentAt: string;
  subject: string;
  bodyText: string;
  fromEmail: string;
  /** 送信者設定の今の表示名 */
  fromName: string | null;
  /** グループ削除済みなら null */
  groupName: string | null;
  includeSender: boolean;
};

export type BulkResendRecipientCounts = {
  /** opt-out 以外を小文字アドレスで重複排除した件数。自分宛ての控えを含まない */
  activeContactCount: number;
  companyCount: number;
  optedOutCount: number;
};

export type BulkResendPreviewItem = {
  seriesId: string;
  /** not_found のとき null */
  seriesName: string | null;
  eligibility: BulkResendEligibility;
  /** not_found / archived / no_template のとき null */
  template: BulkResendTemplate | null;
  /** ok / no_recipients のときだけ値がある */
  recipients: BulkResendRecipientCounts | null;
};

export type BulkResendPreviewResponse = {
  /** リクエストの順 */
  items: BulkResendPreviewItem[];
  /** 送れるシリーズだけを数える。控えを数えない */
  recipients: {
    /** 延べ (実行結果の totalRecipients とは別物) */
    totalContacts: number;
    /** 実人数 */
    uniqueRecipients: number;
    /** 2 つ以上のシリーズに含まれるアドレスの数 */
    duplicatedRecipients: number;
  };
};

export type BulkResendItem = {
  seriesId: string;
  /** preview の template.deliveryId */
  templateDeliveryId: string;
  /** 雛型と違うときだけ送る */
  subject?: string;
  /** 雛型と違うときだけ送る */
  bodyText?: string;
};

export type BulkResendErrorCode =
  | "SERIES_NOT_FOUND"
  | "SERIES_ARCHIVED"
  | "INVALID_TEMPLATE"
  | "TEMPLATE_GROUP_DELETED"
  | "SENDER_UNVERIFIED"
  | "NO_RECIPIENTS"
  | "START_FAILED";

export type BulkResendResultStatus = "started" | "failed" | "unconfirmed";

export type BulkResendResult = {
  seriesId: string;
  /** SERIES_NOT_FOUND のとき null */
  seriesName: string | null;
  status: BulkResendResultStatus;
  /** started / unconfirmed のとき値がある */
  deliveryId: string | null;
  /** started のとき値がある (控えを含む実数) */
  totalRecipients: number | null;
  /** failed のとき値がある */
  errorCode: BulkResendErrorCode | null;
};

export type BulkResendResponse = {
  /** リクエストの順 */
  results: BulkResendResult[];
};

/**
 * 一括再配信の確認内容 (preview) を取得する hook.
 * エラーは RestApiError (または通信断の TypeError) のまま呼び出し側へ投げる.
 * @return {object} preview
 */
export function useBulkResendPreview(): {
  preview: (seriesIds: string[]) => Promise<BulkResendPreviewResponse>;
} {
  const preview = useCallback(
    (seriesIds: string[]) =>
      restClient.post<BulkResendPreviewResponse>(
        "/delivery-series/bulk-resend/preview",
        { seriesIds },
      ),
    [],
  );
  return { preview };
}

/**
 * 一括再配信を実行する hook (作成から起動まで 1 回).
 * エラーは RestApiError (または通信断の TypeError) のまま呼び出し側へ投げる.
 * 429 / その他 / 結果不明の分類は呼び出し側で行う.
 * @return {object} resend
 */
export function useBulkResend(): {
  resend: (items: BulkResendItem[]) => Promise<BulkResendResponse>;
} {
  const resend = useCallback(
    (items: BulkResendItem[]) =>
      restClient.post<BulkResendResponse>("/delivery-series/bulk-resend", {
        items,
      }),
    [],
  );
  return { resend };
}

/**
 * 任意の error を RestApiError に統一する
 * @param {unknown} e 発生した error
 * @return {RestApiError} ラップされた error
 */
function toApiError(e: unknown): RestApiError {
  if (e instanceof RestApiError) return e;
  return new RestApiError(
    e instanceof Error ? e.message : "Unknown error",
    500,
  );
}
